import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Plus, Trash2, ShieldCheck, ShieldX, Clock, Download, FilterX, Sparkles, Heart, Check, X, Star } from "lucide-react";

function StatCard({ label, value }) {
  return (
    <div className="p-5 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">{label}</div>
      <div className="font-display text-3xl mt-2">{value ?? "—"}</div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "Test@123", mobile: "", gender: "male", is_premium: false, kyc_verified: false });

  const load = async () => {
    try {
      const u = await api.get("/admin/users", { params: { q } });
      setUsers(u.data.users);
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const search = (e) => { e.preventDefault(); load(); };
  const toggle = async (u, field) => {
    try { await api.patch(`/admin/users/${u.id}`, { [field]: !u[field] }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const create = async () => {
    try {
      const { data } = await api.post("/admin/users", newUser);
      toast.success("User created. Opening full profile editor…");
      setOpen(false);
      setNewUser({ name: "", email: "", password: "Test@123", mobile: "", gender: "male", is_premium: false, kyc_verified: false });
      load();
      // Deep-link admin to the FULL PROFILE EDITOR for the new user
      window.location.href = `/admin/users/${data.id}/edit`;
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <form onSubmit={search} className="flex gap-2">
          <Input
            data-testid="admin-search"
            placeholder="Search by name, email, mobile, or Member ID (USG…)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-[380px] max-w-full"
          />
          <Button data-testid="admin-search-btn" type="submit" variant="outline"><Search className="w-4 h-4" /></Button>
          {q && (
            <Button
              data-testid="admin-search-clear"
              type="button"
              variant="ghost"
              onClick={() => { setQ(""); setTimeout(() => load(), 0); }}
              className="text-[#57534E]"
            >Clear</Button>
          )}
        </form>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-user-btn" className="btn-pill bg-[#1C1917] text-white hover:bg-[#111]">
              <Plus className="w-4 h-4 mr-1.5" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add new user</DialogTitle><DialogDescription>Curate a profile directly.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="new-name" placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
              <Input data-testid="new-email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
              <Input data-testid="new-mobile" placeholder="Mobile" value={newUser.mobile} onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })} />
              <Input data-testid="new-password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="g" checked={newUser.gender === "male"} onChange={() => setNewUser({ ...newUser, gender: "male" })} /> Male</label>
                <label className="flex items-center gap-2 text-sm"><input type="radio" name="g" checked={newUser.gender === "female"} onChange={() => setNewUser({ ...newUser, gender: "female" })} /> Female</label>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newUser.is_premium} onChange={(e) => setNewUser({ ...newUser, is_premium: e.target.checked })} /> Premium</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newUser.kyc_verified} onChange={(e) => setNewUser({ ...newUser, kyc_verified: e.target.checked })} /> KYC Verified</label>
            </div>
            <DialogFooter>
              <Button data-testid="new-user-submit" onClick={create} className="bg-[#C85A53] hover:bg-[#A94842] text-white">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border overflow-hidden" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ background: "var(--umg-secondary)" }}>
              <th className="p-3 font-semibold">Name</th>
              <th className="p-3 font-semibold">Member ID</th>
              <th className="p-3 font-semibold">Email</th>
              <th className="p-3 font-semibold">Mobile</th>
              <th className="p-3 font-semibold">Gender</th>
              <th className="p-3 font-semibold">Premium</th>
              <th className="p-3 font-semibold">KYC</th>
              <th className="p-3 font-semibold">Ban</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-row-${u.id}`} style={{ borderTop: "1px solid var(--umg-border)" }}>
                <td className="p-3">
                  <a href={`/admin/users/${u.id}/edit`} data-testid={`admin-view-${u.id}`} className="hover:underline text-[#C85A53] font-medium">
                    {u.name}
                  </a>
                </td>
                <td className="p-3">
                  {u.member_id ? (
                    <button
                      data-testid={`member-id-${u.id}`}
                      onClick={() => { navigator.clipboard?.writeText(u.member_id); toast.success(`Copied ${u.member_id}`); }}
                      title="Click to copy"
                      className="text-[11px] text-[#7C3AED] font-mono font-semibold hover:underline"
                    >
                      {u.member_id}
                    </button>
                  ) : (
                    <span className="text-[10px] text-[color:var(--umg-text-2)]">—</span>
                  )}
                </td>
                <td className="p-3 text-[color:var(--umg-text-2)]">{u.email}</td>
                <td className="p-3 text-[color:var(--umg-text-2)] font-mono text-xs">{u.mobile || "—"}</td>
                <td className="p-3">{u.gender}</td>
                <td className="p-3"><button data-testid={`toggle-premium-${u.id}`} onClick={() => toggle(u, "is_premium")} className={`px-2 py-1 rounded-full text-xs ${u.is_premium ? "bg-[#C85A53] text-white" : "bg-[#EAE6DF] dark:bg-neutral-800"}`}>{u.is_premium ? "Premium" : "Free"}</button></td>
                <td className="p-3"><button data-testid={`toggle-kyc-${u.id}`} onClick={() => toggle(u, "kyc_verified")} className={`px-2 py-1 rounded-full text-xs ${u.kyc_verified ? "bg-[#4F7942] text-white" : "bg-[#EAE6DF] dark:bg-neutral-800"}`}>{u.kyc_verified ? "Verified" : "Pending"}</button></td>
                <td className="p-3"><button data-testid={`toggle-ban-${u.id}`} onClick={() => toggle(u, "banned")} className={`px-2 py-1 rounded-full text-xs ${u.banned ? "bg-black text-white" : "bg-[#EAE6DF] dark:bg-neutral-800"}`}>{u.banned ? "Banned" : "Active"}</button></td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <a href={`/admin/users/${u.id}/edit`} data-testid={`admin-edit-${u.id}`} className="text-[#7C3AED] hover:text-[#5B21B6] text-xs font-semibold underline">Edit full profile</a>
                    <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} className="text-[#C85A53] hover:text-[#A94842]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[color:var(--umg-text-2)]">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KycTab() {
  const [data, setData] = useState({ requests: [], manual: [] });
  const [filter, setFilter] = useState("pending");
  const [provider, setProvider] = useState("");
  const [providers, setProviders] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reasonOpen, setReasonOpen] = useState(null);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const params = { status: filter };
      if (provider) params.provider = provider;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/admin/kyc/queue", { params });
      setData(r.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, provider, dateFrom, dateTo]);
  useEffect(() => { api.get("/admin/kyc/providers").then(({ data }) => setProviders(data.providers || [])).catch(() => {}); }, []);

  const approve = async (rid) => { try { await api.post(`/admin/kyc/${rid}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatError(e)); } };
  const reject = async () => { try { await api.post(`/admin/kyc/${reasonOpen}/reject`, { reason }); toast.success("Rejected"); setReasonOpen(null); setReason(""); load(); } catch (e) { toast.error(formatError(e)); } };
  const approveManual = async (userId) => { try { await api.post(`/admin/kyc/manual/${userId}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatError(e)); } };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ status: filter });
      if (provider) params.set("provider", provider);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const token = localStorage.getItem("umg_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/kyc/export.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `umaang_kyc_${filter}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("CSV export failed"); }
  };

  const clearFilters = () => { setProvider(""); setDateFrom(""); setDateTo(""); };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "pending", label: "Pending", icon: Clock },
          { key: "approved", label: "Approved", icon: ShieldCheck },
          { key: "rejected", label: "Rejected", icon: ShieldX },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`kyc-filter-${t.key}`}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5 border transition ${
              filter === t.key ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button data-testid="kyc-export-csv" size="sm" variant="outline" onClick={exportCsv} className="rounded-full border-[color:var(--umg-border)] h-8 text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Advanced filters */}
      <div className="mb-4 p-3 border flex items-center gap-3 flex-wrap" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">Advanced</div>
        <select
          data-testid="kyc-provider-filter"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-full border h-8 bg-transparent"
          style={{ borderColor: "var(--umg-border)" }}
        >
          <option value="">All providers</option>
          {providers.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label className="text-xs flex items-center gap-2">
          From
          <Input data-testid="kyc-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-40" />
        </label>
        <label className="text-xs flex items-center gap-2">
          To
          <Input data-testid="kyc-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-40" />
        </label>
        {(provider || dateFrom || dateTo) && (
          <Button data-testid="kyc-clear-filters" size="sm" variant="ghost" onClick={clearFilters} className="text-xs h-8">
            <FilterX className="w-3.5 h-3.5 mr-1.5" /> Clear
          </Button>
        )}
      </div>

      {filter === "pending" && data.manual?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-3">Manual submissions (no Digio request)</h4>
          <div className="border overflow-hidden" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ background: "var(--umg-secondary)" }}>
                  <th className="p-3">User</th><th className="p-3">Email</th><th className="p-3">Aadhaar</th><th className="p-3">PAN</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.manual.map((m) => (
                  <tr key={m.user_id} data-testid={`kyc-manual-${m.user_id}`} style={{ borderTop: "1px solid var(--umg-border)" }}>
                    <td className="p-3">{m.user_name}</td>
                    <td className="p-3 text-[color:var(--umg-text-2)]">{m.user_email}</td>
                    <td className="p-3 font-mono text-xs">{m.aadhaar_hint || "—"}</td>
                    <td className="p-3 font-mono text-xs">{m.pan_hint || "—"}</td>
                    <td className="p-3">
                      <Button data-testid={`approve-manual-${m.user_id}`} size="sm" onClick={() => approveManual(m.user_id)} className="btn-pill bg-[#4F7942] hover:bg-[#3d5f34] text-white h-8 text-xs">
                        Approve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h4 className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-3">
        {data.requests.length} {filter} request{data.requests.length === 1 ? "" : "s"}
      </h4>
      <div className="border overflow-hidden" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left" style={{ background: "var(--umg-secondary)" }}>
              <th className="p-3">Request</th><th className="p-3">User</th><th className="p-3">Provider</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.requests.map((r) => (
              <tr key={r.request_id} data-testid={`kyc-row-${r.request_id}`} style={{ borderTop: "1px solid var(--umg-border)" }}>
                <td className="p-3 font-mono text-[11px]">{r.request_id.slice(0, 16)}…</td>
                <td className="p-3">
                  <div>{r.user_name}</div>
                  <div className="text-[10px] text-[color:var(--umg-text-2)]">{r.user_email}</div>
                </td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "var(--umg-secondary)" }}>{r.provider}</span></td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${r.verified ? "bg-[#4F7942] text-white" : r.status === "rejected" ? "bg-black text-white" : "bg-[#EAE6DF] dark:bg-neutral-800"}`}>
                    {r.verified ? "verified" : r.status}
                  </span>
                </td>
                <td className="p-3 text-[10px] text-[color:var(--umg-text-2)]">{r.created_at?.slice(0, 16)}</td>
                <td className="p-3 space-x-2">
                  {!r.verified && r.status !== "rejected" && (
                    <>
                      <Button data-testid={`approve-${r.request_id}`} size="sm" onClick={() => approve(r.request_id)} className="btn-pill bg-[#4F7942] hover:bg-[#3d5f34] text-white h-8 text-xs">
                        Approve
                      </Button>
                      <Button data-testid={`reject-${r.request_id}`} size="sm" variant="outline" onClick={() => { setReasonOpen(r.request_id); setReason(""); }} className="btn-pill border-[#C85A53] text-[#C85A53] h-8 text-xs">
                        Reject
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {data.requests.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[color:var(--umg-text-2)]">No {filter} requests.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reasonOpen} onOpenChange={(v) => !v && setReasonOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject KYC request</DialogTitle><DialogDescription>Optional note for internal records.</DialogDescription></DialogHeader>
          <Input data-testid="reject-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button data-testid="reject-confirm" onClick={reject} className="bg-[#C85A53] hover:bg-[#A94842] text-white">Confirm reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MaintenanceTab() {
  const [prefix, setPrefix] = useState("TEST_it6_");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const prefixes = prefix.split(",").map((p) => p.trim()).filter(Boolean);
      const { data } = await api.post("/admin/users/bulk-delete", { prefixes });
      setResult(data);
      toast.success(`Removed ${data.deleted} disposable users`);
      setConfirmOpen(false);
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="p-6 border" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--umg-text-2)]">
          <Sparkles className="w-3.5 h-3.5" /> Bulk cleanup — disposable test users
        </div>
        <p className="mt-3 text-sm text-[color:var(--umg-text-2)]">
          Deletes users whose email <b>starts with</b> the given prefix(es) — plus every associated like, message, connection, view, block, shortlist, report and KYC row.
          Admins are protected. Minimum prefix length is 3 characters.
        </p>
        <div className="mt-5">
          <Label>Prefixes (comma-separated)</Label>
          <Input
            data-testid="bulk-prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="TEST_it6_, ui_m_, ui_f_"
            className="mt-2"
          />
          <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1">
            Example: <code>TEST_it6_, ui_m_, ui_f_</code>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button data-testid="bulk-clean-btn" onClick={() => setConfirmOpen(true)} disabled={busy || prefix.trim().length < 3} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">
            <Trash2 className="w-4 h-4 mr-1.5" /> Clean up matching users
          </Button>
        </div>
        {result && (
          <div data-testid="bulk-clean-result" className="mt-6 p-4 border" style={{ borderRadius: 6, borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
            <div className="text-sm font-semibold">Deleted {result.deleted} users</div>
            <div className="text-xs text-[color:var(--umg-text-2)] mt-1">Prefixes: {result.prefixes.join(", ")}</div>
            {result.matched_emails?.length > 0 && (
              <ul className="mt-3 max-h-40 overflow-y-auto text-xs space-y-0.5">
                {result.matched_emails.map((e) => <li key={e} className="font-mono">· {e}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm bulk delete</DialogTitle>
            <DialogDescription>
              This permanently removes users whose email starts with: <b>{prefix}</b> and all their data. Not reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="bulk-confirm" onClick={run} disabled={busy} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">
              {busy ? "Cleaning…" : "Confirm delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Extract 11-char YouTube video id from watch / shorts / youtu.be URLs
function ytIdFrom(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?[^ ]*v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}


// ---------- Success Stories Tab ----------
function StoriesTab() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending | approved
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ couple_names: "", story: "", city: "", wedding_date: "", photo_url: "", youtube_url: "", featured: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/admin/success-stories", { params });
      setStories(data.stories || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const submit = async () => {
    if (form.couple_names.trim().length < 3) { toast.error("Enter couple names"); return; }
    if (form.story.trim().length < 20) { toast.error("Story must be at least 20 characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/success-stories", form);
      toast.success("Story published to landing page");
      setAddOpen(false);
      setForm({ couple_names: "", story: "", city: "", wedding_date: "", photo_url: "", youtube_url: "", featured: false });
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const patch = async (id, body, okMsg) => {
    try { await api.patch(`/admin/success-stories/${id}`, body); toast.success(okMsg || "Updated"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    try { await api.delete(`/admin/success-stories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {["all", "pending", "approved"].map((f) => (
            <button
              key={f}
              data-testid={`stories-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm border transition ${filter === f ? "bg-[#C85A53] text-white border-[#C85A53]" : "bg-white border-[#D6D2C9] hover:bg-[#FAF7F2]"}`}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <Button data-testid="add-story-btn" onClick={() => setAddOpen(true)} className="btn-pill bg-gradient-to-r from-[#C85A53] to-[#DB2777] hover:opacity-90 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Add story
        </Button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {stories.length === 0 && (
          <div className="col-span-full text-center text-sm text-[color:var(--umg-text-2)] py-12 border border-dashed rounded-xl">
            No stories in this filter. Click <b>Add story</b> to publish one.
          </div>
        )}
        {stories.map((s) => (
          <div key={s.id} data-testid={`story-row-${s.id}`} className="p-5 border" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
            <div className="flex gap-4">
              {s.youtube_thumbnail ? (
                <div className="relative w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-black">
                  <img src={s.youtube_thumbnail} alt="" className="w-full h-full object-cover opacity-90" />
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="w-8 h-8 rounded-full bg-white/95 grid place-items-center">
                      <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[8px] border-l-[#C85A53] border-b-[5px] border-b-transparent ml-0.5" />
                    </div>
                  </div>
                </div>
              ) : s.photo_url ? (
                <img src={s.photo_url} alt="" className="w-24 h-24 object-cover rounded-lg shrink-0" onError={(e) => e.currentTarget.style.display="none"} />
              ) : (
                <div className="w-24 h-24 shrink-0 rounded-lg bg-[#FAF7F2] grid place-items-center">
                  <Heart className="w-8 h-8 text-[#C85A53]/50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-lg">{s.couple_names}</div>
                  {s.featured && <span className="text-[10px] uppercase tracking-widest bg-[#FEF3C7] text-[#7C2D12] px-2 py-0.5 rounded-full">Featured</span>}
                  {s.youtube_id && <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Video</span>}
                  {s.approved ? (
                    <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">Live</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Pending</span>
                  )}
                </div>
                <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">
                  {[s.city, s.wedding_date].filter(Boolean).join(" · ")}
                  {s.submitted_by_name && !s.approved && <> · Submitted by <b>{s.submitted_by_name}</b></>}
                </div>
                <p className="mt-2 text-sm text-[#3C3936] line-clamp-3">{s.story}</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {!s.approved && (
                    <Button data-testid={`approve-${s.id}`} size="sm" onClick={() => patch(s.id, { approved: true }, "Approved & live")} className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  {s.approved && (
                    <Button data-testid={`unapprove-${s.id}`} size="sm" variant="outline" onClick={() => patch(s.id, { approved: false }, "Unpublished")} className="h-8 px-3 text-xs">
                      <X className="w-3 h-3 mr-1" /> Unpublish
                    </Button>
                  )}
                  <Button data-testid={`feature-${s.id}`} size="sm" variant="outline" onClick={() => patch(s.id, { featured: !s.featured }, s.featured ? "Un-featured" : "Featured")} className="h-8 px-3 text-xs">
                    <Star className={`w-3 h-3 mr-1 ${s.featured ? "fill-[#FBBF24] text-[#FBBF24]" : ""}`} /> {s.featured ? "Un-feature" : "Feature"}
                  </Button>
                  <Button data-testid={`delete-story-${s.id}`} size="sm" variant="outline" onClick={() => del(s.id)} className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a new success story</DialogTitle>
            <DialogDescription>Publishes directly to the landing carousel (auto-approved).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Couple names</Label>
              <Input data-testid="story-couple-names" className="mt-1" value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} placeholder="e.g. Priya & Arjun" />
            </div>
            <div>
              <Label>Their story</Label>
              <textarea
                data-testid="story-text"
                className="mt-1 w-full border rounded-md p-2 text-sm min-h-[120px]"
                style={{ borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}
                value={form.story}
                onChange={(e) => setForm({ ...form, story: e.target.value })}
                placeholder="How did they meet on Umaang? What made the match special? Family reactions?"
                maxLength={2000}
              />
              <div className="text-[10px] text-[color:var(--umg-text-2)] mt-0.5">{form.story.length}/2000 characters</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City</Label>
                <Input data-testid="story-city" className="mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Delhi" />
              </div>
              <div>
                <Label>Wedding date</Label>
                <Input data-testid="story-wedding-date" type="date" className="mt-1" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Photo URL <span className="text-xs text-[color:var(--umg-text-2)]">(optional if video below)</span></Label>
              <Input data-testid="story-photo-url" className="mt-1" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…jpg (public URL or data URI)" />
              {form.photo_url && (
                <img src={form.photo_url} alt="" className="mt-2 h-32 object-cover rounded-lg" onError={(e) => e.currentTarget.style.display="none"} />
              )}
            </div>
            <div>
              <Label>YouTube video URL <span className="text-xs text-[color:var(--umg-text-2)]">(optional — overrides photo on landing)</span></Label>
              <Input
                data-testid="story-youtube-url"
                className="mt-1"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://youtu.be/… or https://www.youtube.com/watch?v=…"
              />
              {(() => {
                const yid = ytIdFrom(form.youtube_url);
                if (form.youtube_url && !yid) {
                  return <div className="text-[11px] text-amber-700 mt-1">Not a valid YouTube URL. Use youtu.be, /watch?v=, /shorts/ or /embed/ links.</div>;
                }
                if (yid) {
                  return (
                    <div className="relative mt-2 h-32 w-56 rounded-lg overflow-hidden bg-black">
                      <img src={`https://img.youtube.com/vi/${yid}/hqdefault.jpg`} alt="Video preview" className="w-full h-full object-cover opacity-90" />
                      <div className="absolute inset-0 grid place-items-center">
                        <div className="w-10 h-10 rounded-full bg-white/95 grid place-items-center">
                          <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-[#C85A53] border-b-[6px] border-b-transparent ml-1" />
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <label className="flex items-center gap-2 text-sm select-none">
              <input data-testid="story-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              <Star className="w-4 h-4 text-[#F59E0B]" fill={form.featured ? "#F59E0B" : "none"} />
              Feature at the top of the carousel
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="story-submit" onClick={submit} disabled={busy} className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white">
              {busy ? "Publishing…" : "Publish story"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="uppercase tracking-[0.28em] text-xs text-[#C85A53] font-semibold">Umaang Admin</div>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Control room</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Users" value={stats.users} />
        <StatCard label="Premium" value={stats.premium} />
        <StatCard label="KYC verified" value={stats.kyc_verified} />
        <StatCard label="Banned" value={stats.banned} />
        <StatCard label="Messages" value={stats.messages} />
        <StatCard label="Likes" value={stats.likes} />
      </div>

      <Tabs defaultValue="users" className="mt-10">
        <TabsList className="rounded-full bg-[color:var(--umg-secondary)] p-1">
          <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-full">Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-kyc" value="kyc" className="rounded-full">KYC Queue</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-stories" value="stories" className="rounded-full">Success stories</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-maintenance" value="maintenance" className="rounded-full">Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="kyc" className="mt-6"><KycTab /></TabsContent>
        <TabsContent value="stories" className="mt-6"><StoriesTab /></TabsContent>
        <TabsContent value="maintenance" className="mt-6"><MaintenanceTab /></TabsContent>
      </Tabs>
    </div>
  );
}
